import {useLayoutEffect} from 'react';

/** CSS custom property published on `<html>` while the PDP sticky ATC strip is mounted. */
export const PDP_ATC_STRIP_HEIGHT_VAR = '--pdp-atc-strip-height';

const publishStripHeight = (desktopEl, mobileEl) => {
  if (typeof document === 'undefined') return;

  const height = desktopEl?.offsetHeight || mobileEl?.offsetHeight || 0;

  document.documentElement.style.setProperty(PDP_ATC_STRIP_HEIGHT_VAR, `${height}px`);
};

const clearStripHeight = () => {
  if (typeof document === 'undefined') return;

  document.documentElement.style.removeProperty(PDP_ATC_STRIP_HEIGHT_VAR);
};

/**
 * Measures the visible PDP sticky add-to-cart strip and publishes its height as
 * `--pdp-atc-strip-height` so the site footer can clear the bar (DE-159).
 *
 * Desktop and mobile strips are separate nodes (`display: none` on the inactive
 * breakpoint); we publish whichever currently has a non-zero `offsetHeight`.
 *
 * @param {React.RefObject<HTMLElement>} desktopRef
 * @param {React.RefObject<HTMLElement>} mobileRef
 * @param {boolean} [enabled=true] When false, clears the CSS variable (strip unmounted).
 */
export function usePdpAtcStripHeight(desktopRef, mobileRef, enabled = true) {
  useLayoutEffect(() => {
    if (!enabled) {
      clearStripHeight();

      return undefined;
    }

    const desktopEl = desktopRef?.current;
    const mobileEl = mobileRef?.current;

    if (!desktopEl && !mobileEl) return undefined;

    const publish = () => publishStripHeight(desktopRef?.current, mobileRef?.current);

    publish();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publish);

      return () => {
        window.removeEventListener('resize', publish);
        clearStripHeight();
      };
    }

    const observer = new ResizeObserver(publish);

    if (desktopEl) observer.observe(desktopEl);
    if (mobileEl) observer.observe(mobileEl);

    window.addEventListener('resize', publish);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publish);
      clearStripHeight();
    };
  }, [desktopRef, mobileRef, enabled]);
}
